<template>
  <UInput
    ref="input"
    v-bind="{ ...forwardedProps, ...$attrs }"
    :autocomplete="props.autocomplete ?? 'on'"
    :color="color"
    :aria-busy="loading || undefined"
    :loading="false"
    :draggable="false"
    @update:model-value="emit('update:modelValue', $event)"
    @blur="emit('blur', $event)"
    @change="emit('change', $event)"
    class="p-input"
    @dragstart.prevent
  >
    <template v-if="hasLeading()" #leading="slotProps">
      <slot name="leading" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="leadingIcon"
          :icon="leadingIcon"
          aria-hidden="true"
          :class="[
            slotProps.ui.leadingIcon({ class: props.ui?.leadingIcon }),
            { 'animate-spin': loading }
          ]"
        />
      </slot>
    </template>
    <template v-if="slots.default" #default="slotProps">
      <slot v-bind="slotProps" />
    </template>
    <template v-if="hasTrailing()" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="trailingIcon"
          :icon="trailingIcon"
          aria-hidden="true"
          :class="[
            slotProps.ui.trailingIcon({ class: props.ui?.trailingIcon }),
            { 'animate-spin': loading && !leadingIcon }
          ]"
        />
      </slot>
    </template>
  </UInput>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue"
import { reactiveOmit } from "@vueuse/core"
import NuxtInput from "@nuxt/ui/components/Input.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import type { PInputProps, PInputSlots } from "./input"

const UInput = NuxtInput<string | null, NonNullable<PInputProps["modelModifiers"]>>
const props = withDefaults(defineProps<PInputProps>(), {
  color: "secondary",
  variant: "outline",
  trailing: undefined
})
const emit = defineEmits<{
  "update:modelValue": [value: string | null | undefined]
  blur: [event: FocusEvent]
  change: [event: Event]
}>()
const slots = defineSlots<PInputSlots>()
const forwardedProps = reactiveOmit(
  props,
  "icon",
  "leadingIcon",
  "trailingIcon",
  "loadingIcon",
  "loading"
)
const isLeading = computed(() =>
  Boolean(
    props.leadingIcon ||
    (props.icon && (props.leading || !props.trailing)) ||
    (props.loading && !props.trailing)
  )
)
const leadingIcon = computed(() =>
  isLeading.value
    ? ((props.loading
        ? (props.loadingIcon ?? faSpinner)
        : (props.leadingIcon ?? props.icon)) as IconProp)
    : undefined
)
const trailingIcon = computed(() =>
  (props.icon && props.trailing) ||
  (props.loading && props.trailing && !isLeading.value) ||
  (props.trailingIcon && props.trailing !== false)
    ? ((props.loading && !isLeading.value
        ? (props.loadingIcon ?? faSpinner)
        : (props.trailingIcon ?? props.icon)) as IconProp)
    : undefined
)
function hasLeading() {
  return !!slots.leading || !!leadingIcon.value
}
function hasTrailing() {
  return !!slots.trailing || !!trailingIcon.value
}
const input = useTemplateRef<{ inputRef: HTMLInputElement | null }>("input")
defineOptions({ inheritAttrs: false })
defineExpose({
  get inputRef() {
    return input.value?.inputRef
  },
  focus: () => input.value?.inputRef?.focus(),
  blur: () => input.value?.inputRef?.blur(),
  select: () => input.value?.inputRef?.select()
})
</script>
